import SiteConfig from '@/services/SiteConfig'

// Fields copied 1:1 from a /v2 products-search doc onto MetaProduct.
const PASSTHROUGH_FIELDS = [
  'product_id',
  'campaign_id',
  'final_price',
  'score_info',
  'manufacturer_url',
  'milesandmore',
  'delivery',
  'is_enabled',
  'awards',
  'legal_information_string',
  // nutritional_note drives the organic/vegan badges; requires the
  // platform-v2 indexer to expose `details` in the search doc.
  'details',
  'name',
  'type',
  'catch_phrase',
  'reason_why_one',
  'attribute_set',
  'contains_sulfide',
  'campaign_banner',
  'first_enabled_at',
  'alcohol_content',
  'unit_type'
]

/**
 * Resolve a PDP path from a search doc, most-specific source first:
 *   1. url_rewrites[0].request_uri — global products-search docs.
 *   2. full_url — the collection-page products-search endpoint returns an empty
 *      url_rewrites and exposes the full PDP path (slug + `-pdp-p…-c…`) here
 *      instead. (The bare `url` field is only the slug without the -pdp suffix,
 *      so it is NOT a valid PDP link.)
 *   3. id-based fallback — the PDP route (`/:prefix*-pdp-p:pId-c:cId`) allows an
 *      empty prefix, so a bare `-pdp-p<id>-c<id>` always resolves. This keeps
 *      tiles clickable even if both slug fields are missing.
 */
const _resolvePdpPath = (doc) => {
  const requestUri = doc.url_rewrites?.[0]?.request_uri
  if (requestUri) return requestUri
  if (doc.full_url) return doc.full_url
  if (doc.product_id != null && doc.campaign_id != null) {
    return `-pdp-p${doc.product_id}-c${doc.campaign_id}`
  }
  return null
}

/**
 * v2 search delivers tags as a comma-separated string; Tags.vue (and the rest
 * of the FE) expect the array shape meta/bulk products carry. Iterating a raw
 * string in Tags.vue's v-for renders one box per character, and String(null)
 * would surface a literal "null" tag — so null/empty collapse to an empty array.
 */
const _normalizeTags = (tags) => {
  if (Array.isArray(tags)) return tags
  if (tags === null || tags === '') return []
  return String(tags)
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

/**
 * Maps a /v2 products-search doc to MetaProduct field names. Only defined
 * values are returned so MetaProductSchema defaults are never overridden
 * with undefined. Legacy (solr) docs pass through with whatever subset of
 * these fields they carry — their bulk-fetch enrichment stays untouched.
 */
export const mapSearchProduct = (doc) => {
  if (!doc) return {}

  // Marks products originating from the search endpoint — only these may
  // skip the meta/bulk fetch (see resolveSearchProduct); other payloads
  // (cart upsell, PDP meta products) keep their bulk enrichment.
  const mapped = { from_product_search: true }
  for (const field of PASSTHROUGH_FIELDS) {
    if (doc[field] !== undefined) mapped[field] = doc[field]
  }

  // Only rich v2 docs carry the structured price object — never map legacy
  // scalar prices.
  if (doc.price?.final_formatted !== undefined) mapped.price = doc.price

  if (doc.manufacturer_name !== undefined) {
    mapped.manufacturer = doc.manufacturer_name
  }
  if (doc.customer_rating !== undefined) {
    mapped.ratings_average = doc.customer_rating
  }
  if (doc.customer_rating_count !== undefined) {
    mapped.ratings_count = doc.customer_rating_count
  }
  if (doc.wine_alcohol_concentration !== undefined) {
    mapped.nutritional_data = {
      wine_alcohol_concentration: doc.wine_alcohol_concentration
    }
  }
  // Tile CTA derives its qty options from qty.values (meta/bulk shape);
  // unit labels fall back to unit_type in SelectOptionFactory.
  if (doc.qty_increments !== undefined) {
    mapped.qty = { values: doc.qty_increments }
  }
  if (doc.tags !== undefined) mapped.tags = _normalizeTags(doc.tags)

  const pdpPath = _resolvePdpPath(doc)
  if (pdpPath) mapped.url = SiteConfig.pageRoot + pdpPath

  return mapped
}

export default { mapSearchProduct }
